
import {
  computed, defineComponent, onBeforeMount, reactive, watch,
} from 'vue';
import { useAppBase } from '@/core/composables/AppBase';
import ComunicacaoApi from '@/core/components/UI/ComunicacaoApi.vue';
import { ICliente } from '@/models/Entidades/ICliente';
import { IPedido, IPedidoItem } from '@/models/Entidades/IPedido';
import { EStatusResposta, IResposta } from '@/core/models/IResposta';
import ServicoCliente from '@/servicos/ServicoCliente';
import { EStatusPedido } from '@/models/Enumeradores/EStatusPedido';
import SelecionarData from '@/core/components/UI/SelecionarData.vue';
import ComboFormaPagamento from '@/components/FormasPagamentos/ComboFormaPagamento.vue';
import ComboStatusPedido from '@/components/Pedidos/ComboStatusPedido.vue';
import { IDTOBuscaProdutoOtimizada } from '@/models/DTO/IDTOBuscaProdutoOtimizada';
import { IProdutoBuscaOtimizadaParametrosConsulta } from '@/models/ParametrosRequisicao/Consulta/IProdutoBuscaOtimizadaParametrosConsulta';
import ServicoProduto from '@/servicos/ServicoProduto';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import Icone from '@/core/components/UI/Icone.vue';
import ServicoPedido from '@/servicos/ServicoPedido';
import storeApp from '@/store/storeApp';
import { ISalvamentoAutomatico } from '@/core/models/ISalvamentoAutomatico';
import ServicoApp from '@/servicos/ServicoApp';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import { IRespostaPedido } from '@/core/models/IRespostaPedido';
import { IDTOProdutoDefinicaoVenderMultiplos } from '@/models/DTO/IDTOProdutoDefinicaoVenderMultiplos';

type TTelaPedido = 'dados-principais' | 'item';
type TAbaAtendimento = 'pedido' | 'itens' | 'buscarProdutos';

interface IItemDigitacao {
  quantidade: number;
  valorUnitario: number;
  percentualDesconto: number;
  valorDesconto: number;
  valorIpi: number;
  valorSt: number;
  valorTotal: number;
}

export default defineComponent({
  name: 'PedidoCadastro',
  components: {
    ComunicacaoApi,
    SelecionarData,
    ComboFormaPagamento,
    ComboStatusPedido,
    Icone,
  },
  setup() {
    const {
      appBase, apresentarComunicacaoApi, ocultarComunicacaoApi, obterCodigoClienteRota, obterCodigoRota, apresentarMensagemSucesso, apresentarMensagemAlerta,
    } = useAppBase();

    const servicoCliente = new ServicoCliente();
    const servicoProduto = new ServicoProduto();
    const servicoPedido = new ServicoPedido();
    const servicoApp = new ServicoApp();

    const state = reactive({
      tela: 'dados-principais' as TTelaPedido,
      aba: 'pedido' as TAbaAtendimento,
      cliente: {} as ICliente,
      pedido: {} as IPedido,
      produtos: [] as IPedidoItem[],
      produtosFiltrados: [] as IPedidoItem[],
      produtoSelecionado: {} as IDTOBuscaProdutoOtimizada,
      item: {} as IItemDigitacao,
      dataPedido: '',
      cotacao: false,
      tipoOperacao: 'Pré-venda',
      planoRecebimento: '',
      tabelaAjuste: 'TABELA DISTRIBUIDOR',
      tipoRetirada: 'A Entregar',
      tipoFrete: 'Nenhum Frete',
      transportadora: '',
      condicaoRecebimento: '',
      percentualDesconto: 0,
      parametrosBuscaProduto: {} as IProdutoBuscaOtimizadaParametrosConsulta,
      produtosBusca: [] as IDTOBuscaProdutoOtimizada[],
      recuperarSalvamentoAutomatico: false,
      salvamentoAutomatico: {} as ISalvamentoAutomatico,
      alterouPedido: false,
      permitirSalvamentoAutomatico: false,
      definicoesVenderMultiplos: [] as IDTOProdutoDefinicaoVenderMultiplos[],
    });

    let debounceBuscaProdutos = 0;
    let debounce:any = 0;
    let debounceSalvamentoAutomatico:any = 0;

    function obtemIdentificadorSalvamentoAutomatico(): string {
      return `PEDIDO_FV_${state.pedido.numeroIdentificacao}`;
    }

    const tituloTela = computed(() => {
      if (state.tela === 'item') {
        return 'Detalhe do Item';
      }

      return 'Pedido';
    });

    const clienteDescricao = computed(() => {
      if (state.cliente.codigo > 0) {
        return `${state.cliente.codigo.toString().padStart(8, '0')} - ${state.cliente.nomeRazaoSocial || ''}`;
      }

      return '';
    });

    const pedidoEmDigitacao = computed(() => state.pedido.status === EStatusPedido.EmDigitacao);

    const apresentarAbaItens = computed(() => state.pedido.itens && state.pedido.itens.length > 0);

    const apresentarAbaBuscarProdutos = computed(() => pedidoEmDigitacao.value);

    const totalItensQuantidade = computed(() => {
      if (!state.pedido.itens) {
        return 0;
      }

      return state.pedido.itens.length;
    });

    const valorProdutos = computed(() => {
      if (!state.pedido.itens) {
        return 0;
      }

      return state.pedido.itens.reduce((total, item) => total + item.valorTotal, 0);
    });

    const valorDesconto = computed(() => {
      if (state.percentualDesconto <= 0) {
        return 0;
      }

      return valorProdutos.value * (state.percentualDesconto / 100);
    });

    async function salvamentoAutomatico() {
      if (!state.permitirSalvamentoAutomatico) {
        return;
      }

      if (!pedidoEmDigitacao.value) {
        return;
      }

      if (!state.pedido || !state.pedido.itens) {
        return;
      }

      if (!UtilitarioGeral.valorValido(state.pedido.numeroIdentificacao)) {
        return;
      }

      state.alterouPedido = true;
      clearTimeout(debounceSalvamentoAutomatico);
      debounceSalvamentoAutomatico = setTimeout(async () => {
        const identificador = obtemIdentificadorSalvamentoAutomatico();
        state.salvamentoAutomatico.data = UtilitarioData.obterDataAtual();
        state.salvamentoAutomatico.codigoParceiro = Number(storeApp.state.parceiroAutenticado.codigo);
        state.salvamentoAutomatico.codigoUsuario = storeApp.state.usuarioAutenticado.codigo;
        state.salvamentoAutomatico.identificador = identificador;
        state.salvamentoAutomatico.dados = JSON.stringify(state.pedido);
        // Salva informações - LocalStorage
        await servicoApp.salvamentoAutomaticoLocalStorage(`${identificador}_${state.salvamentoAutomatico.codigoUsuario}`, state.salvamentoAutomatico);
        // Salva informações - API Servidor
        // const retornoSalvamento = await servicoApp.salvamentoAutomaticoApi(state.salvamentoAutomatico);
        // if (retornoSalvamento.status === EStatusResposta.Sucesso) {
        //            state.salvamentoAutomatico.codigo = retornoSalvamento.codigo;
        //          }
        state.recuperarSalvamentoAutomatico = false;
      }, 1000);
    }

    function produtoJaAdicionado(codigoProdutoDefinicao: number) {
      return state.pedido.itens.some((x) => x.codigoProdutoDefinicao === codigoProdutoDefinicao);
    }

    function calcularTotais() {
      state.pedido.valorDesconto = valorDesconto.value;
      state.pedido.valorTotal = valorProdutos.value - state.pedido.valorDesconto + state.pedido.valorFrete + state.pedido.valorAcrescimos;
    }

    function calcularItem() {
      if (!state.produtoSelecionado) {
        return;
      }

      const quantidade = Number(state.item.quantidade || 1);
      const percentualDesconto = Number(state.item.percentualDesconto || 0);
      const subtotal = quantidade * state.produtoSelecionado.precoUnitario;

      state.item.valorDesconto = subtotal * (percentualDesconto / 100);
      // state.item.valorIpi = (subtotal - state.item.valorDesconto) * (state.produtoSelecionado.aliquotaIpi / 100);
      state.item.valorSt = 0;
      state.item.valorTotal = subtotal - state.item.valorDesconto + state.item.valorIpi + state.item.valorSt;
    }

    function limparPedido() {
      state.alterouPedido = false;
      state.pedido = {} as IPedido;
      state.pedido.codigo = 0;
      state.pedido.data = new Date().toISOString();
      state.pedido.status = EStatusPedido.EmDigitacao;
      state.pedido.codigoVendedor = storeApp.state.codigoVendedor;
      state.pedido.codigoCliente = 0;
      state.pedido.cpfCnpj = '';
      state.pedido.nomeRazaoSocial = '';
      state.pedido.detalhesFormaEntrega = '';
      state.pedido.prazoEntrega = 0;
      state.pedido.detalhesRegraFrete = '';
      state.pedido.codigoRastreamento = '';
      state.pedido.detalhesFormaPagamento = '';
      state.pedido.quantidadeParcelas = 0;
      state.pedido.pesoTotal = 0;
      state.pedido.valorFrete = 0;
      state.pedido.valorDesconto = 0;
      state.pedido.cupomDesconto = '';
      state.pedido.valorAcrescimos = 0;
      state.pedido.valorTotal = 0;
      state.pedido.observacoes = '';
      state.pedido.numeroErp = '';
      state.pedido.chaveAcessoNfe = '';
      state.pedido.historicos = [];
      state.pedido.transacoesPagamento = [];
      state.pedido.itens = [];
      state.dataPedido = new Date().toLocaleDateString('pt-BR');
      state.percentualDesconto = 0;
      state.parametrosBuscaProduto = {} as IProdutoBuscaOtimizadaParametrosConsulta;
      state.produtosBusca = [];
      state.definicoesVenderMultiplos = [];
    }

    function limparItem() {
      state.item = {} as IItemDigitacao;
      state.item.quantidade = 1;
      state.item.valorUnitario = 0;
      state.item.percentualDesconto = 0;
      state.item.valorDesconto = 0;
      state.item.valorIpi = 0;
      state.item.valorSt = 0;
      state.item.valorTotal = 0;
    }

    async function obterCliente(codigoCliente: number) {
      if (codigoCliente <= 0) {
        return;
      }

      appBase.carregando = true;

      state.cliente = await servicoCliente.obter(codigoCliente);

      state.pedido.codigoCliente = state.cliente.codigo;
      state.pedido.cpfCnpj = state.cliente.cpfCnpj;
      state.pedido.nomeRazaoSocial = state.cliente.nomeRazaoSocial;

      appBase.carregando = false;
    }

    async function obterPedido(codigoPedido: number) {
      if (codigoPedido <= 0) {
        return;
      }

      appBase.carregando = true;
      state.pedido = await servicoPedido.obter(codigoPedido);
      if (UtilitarioGeral.validaLista(state.pedido.itens)) {
        state.definicoesVenderMultiplos = await servicoProduto.obterVenderMultiplosPorDefinicoes(state.pedido.itens.map((c) => c.codigoProdutoDefinicao));
      }

      state.alterouPedido = false;
      const identificador = obtemIdentificadorSalvamentoAutomatico();
      state.salvamentoAutomatico = await servicoApp.obterSalvamentoAutomaticoLocalStorage(`${identificador}_${storeApp.state.usuarioAutenticado.codigo}`);

      if (UtilitarioData.validaDataPreenchida(state.salvamentoAutomatico.data) && !UtilitarioData.validaDataPreenchida(state.pedido.dataAlteracao)) {
        state.recuperarSalvamentoAutomatico = true;
      } else if (UtilitarioData.validaDataPreenchida(state.salvamentoAutomatico.data) && UtilitarioData.validaDataPreenchida(state.pedido.dataAlteracao)) {
        if (UtilitarioData.verificaDataMaisRecente(state.salvamentoAutomatico.data, state.pedido.dataAlteracao)) {
          state.recuperarSalvamentoAutomatico = true;
        }
      }

      appBase.carregando = false;
    }

    async function buscarProdutos() {
      if (!pedidoEmDigitacao.value) {
        return;
      }

      clearTimeout(debounceBuscaProdutos);
      debounceBuscaProdutos = setTimeout(async () => {
        state.parametrosBuscaProduto.codigoCliente = state.pedido.codigoCliente;
        state.produtosBusca = await servicoProduto.obterBuscaProdutosOtimizada(state.parametrosBuscaProduto);
      }, 600);
    }

    onBeforeMount(async () => {
      state.permitirSalvamentoAutomatico = false;

      limparPedido();
      limparItem();

      const codigoCliente = obterCodigoClienteRota();
      const codigoPedido = obterCodigoRota();
      if (codigoCliente > 0) {
        await obterCliente(codigoCliente);
        state.pedido.numeroIdentificacao = `PEN_U${storeApp.state.usuarioAutenticado.codigo}_C${state.pedido.codigoCliente}`;

        state.alterouPedido = false;
        const identificador = obtemIdentificadorSalvamentoAutomatico();
        state.salvamentoAutomatico = await servicoApp.obterSalvamentoAutomaticoLocalStorage(`${identificador}_${storeApp.state.usuarioAutenticado.codigo}`);

        if (UtilitarioData.validaDataPreenchida(state.salvamentoAutomatico.data) && !UtilitarioData.validaDataPreenchida(state.pedido.dataAlteracao)) {
          state.recuperarSalvamentoAutomatico = true;
        } else if (UtilitarioData.validaDataPreenchida(state.salvamentoAutomatico.data) && UtilitarioData.validaDataPreenchida(state.pedido.dataAlteracao)) {
          if (UtilitarioData.verificaDataMaisRecente(state.salvamentoAutomatico.data, state.pedido.dataAlteracao)) { state.recuperarSalvamentoAutomatico = true; }
        }
      } else {
        await obterPedido(codigoPedido);
        await obterCliente(state.pedido.codigoCliente);
      }

      await buscarProdutos();

      state.permitirSalvamentoAutomatico = true;
    });

    function novoPedido() {
      state.permitirSalvamentoAutomatico = false;

      const clienteAtual = state.cliente;

      limparPedido();

      state.cliente = clienteAtual;
      state.pedido.codigoCliente = state.cliente.codigo;
      state.pedido.cpfCnpj = state.cliente.cpfCnpj;
      state.pedido.nomeRazaoSocial = state.cliente.nomeRazaoSocial;
      state.pedido.numeroIdentificacao = `PEN_U${storeApp.state.usuarioAutenticado.codigo}_C${state.pedido.codigoCliente}`;
      state.aba = 'pedido';

      state.permitirSalvamentoAutomatico = true;
    }

    function voltarTela() {
      if (state.tela === 'item') {
        state.tela = 'dados-principais';
        state.aba = apresentarAbaItens.value ? 'itens' : 'pedido';
        return;
      }

      window.history.back();
    }

    function cancelarPedido() {
      window.history.back();
    }

    async function salvarPedido() {
      appBase.resposta = {} as IResposta;

      calcularTotais();

      apresentarComunicacaoApi('Aguarde por favor... Estamos salvando as informações do Pedido.');
      let respostaPedido:IRespostaPedido = {} as IRespostaPedido;
      if (state.pedido.codigo === 0) {
        respostaPedido = await servicoPedido.criarPedidoForcaVendas(state.pedido);
      } else {
        respostaPedido = await servicoPedido.atualizarPedidoForcaVendas(state.pedido);
      }
      ocultarComunicacaoApi();
      if (respostaPedido.status === EStatusResposta.Sucesso) {
        // Remove informações - LocalStorage
        servicoApp.removerSalvamentoAutomaticoLocalStorage(`${obtemIdentificadorSalvamentoAutomatico()}_${storeApp.state.usuarioAutenticado.codigo}`);
        apresentarMensagemSucesso(respostaPedido.mensagem);

        await obterPedido(respostaPedido.codigo);
      } else {
        apresentarMensagemAlerta(respostaPedido.mensagem);
      }
    }

    async function enviarPedido() {
      apresentarComunicacaoApi('Aguarde por favor... Estamos enviando o seu Pedido.');
      const resposta = await servicoPedido.enviarPedidoForcaVendas(state.pedido.codigo);
      ocultarComunicacaoApi();
      if (resposta.status === EStatusResposta.Sucesso) {
        apresentarMensagemSucesso(resposta.mensagens[0].subMensagens[0]);
        await obterPedido(state.pedido.codigo);
        state.pedido.status = EStatusPedido.PedidoEfetuado;
      } else {
        apresentarMensagemAlerta(resposta.mensagens[0].subMensagens[0]);
      }
    }

    async function deletarPedido() {
      apresentarComunicacaoApi('Aguarde por favor... Estamos deletando o seu Pedido.');
      const resposta = await servicoPedido.deletarPedidoForcaVendas(state.pedido.codigo);
      ocultarComunicacaoApi();
      if (resposta.status === EStatusResposta.Sucesso) {
        state.pedido.status = EStatusPedido.Excluido;
        // retorna tela pedidos
      } else {
        apresentarMensagemAlerta(resposta.mensagens[0].subMensagens[0]);
      }
    }

    function abrirItem(produto: IDTOBuscaProdutoOtimizada) {
      const itemExistente = state.pedido.itens.find((x) => x.codigoProdutoDefinicao === produto.codigoProdutoDefinicao);

      if (!state.definicoesVenderMultiplos.some((x) => x.codigoProdutoDefinicao === produto.codigoProdutoDefinicao)) {
        state.definicoesVenderMultiplos.push({ codigoProdutoDefinicao: produto.codigoProdutoDefinicao, venderMultiplos: produto.venderMultiplus });
      }

      state.produtoSelecionado = produto;
      limparItem();

      if (itemExistente !== undefined) {
        state.item.quantidade = itemExistente.quantidade;
        state.item.valorUnitario = itemExistente.precoUnitario;
        state.item.valorTotal = itemExistente.valorTotal;
      } else {
        state.item.valorUnitario = produto.precoUnitario;
        if (produto.venderMultiplus > 0) {
          state.item.quantidade = produto.venderMultiplus;
        }
      }

      calcularItem();
      state.tela = 'item';
    }

    function abrirItemPedido(itemPedido: IPedidoItem) {
      const produto = {} as IDTOBuscaProdutoOtimizada;

      produto.codigoProduto = itemPedido.codigoProduto;
      produto.codigoProdutoDefinicao = itemPedido.codigoProdutoDefinicao;
      produto.sku = itemPedido.sku;
      produto.nomeProduto = itemPedido.produto;
      produto.precoUnitario = itemPedido.precoUnitario;
      produto.codigoErp = itemPedido.codigoErp;
      produto.venderMultiplus = 0;

      const indexDefinicaoMultiplos = state.definicoesVenderMultiplos.findIndex((x) => x.codigoProdutoDefinicao === itemPedido.codigoProdutoDefinicao);
      if (indexDefinicaoMultiplos >= 0) {
        produto.venderMultiplus = state.definicoesVenderMultiplos[indexDefinicaoMultiplos].venderMultiplos;
      }

      state.produtoSelecionado = produto;
      limparItem();

      state.item.quantidade = itemPedido.quantidade;
      state.item.valorUnitario = itemPedido.precoUnitario;
      state.item.valorTotal = itemPedido.valorTotal;

      calcularItem();
      state.tela = 'item';
    }

    function removerItemPedido(itemPedido: IPedidoItem) {
      if (!pedidoEmDigitacao.value) {
        return;
      }

      state.pedido.itens = state.pedido.itens.filter((x) => x.codigoProdutoDefinicao !== itemPedido.codigoProdutoDefinicao);

      if (!state.pedido.itens.length) {
        state.aba = 'pedido';
      }

      calcularTotais();
      salvamentoAutomatico();
    }

    function repetirUltimaCompra() {
      if (!state.produtoSelecionado) {
        return;
      }

      // state.item.quantidade = state.produtoSelecionado.ultimaCompraQuantidade || 0;
      // state.item.valorUnitario = state.produtoSelecionado.ultimaCompraValor || state.produtoSelecionado.valorTabela;
      calcularItem();
      salvamentoAutomatico();
    }

    function confirmarItem() {
      if (!pedidoEmDigitacao.value) {
        return;
      }

      if (!state.produtoSelecionado) {
        return;
      }

      calcularItem();

      const itemExistente = state.pedido.itens.find((x) => x.codigoProdutoDefinicao === state.produtoSelecionado?.codigoProdutoDefinicao);
      const precoUnitario = state.produtoSelecionado.precoUnitario;

      if (itemExistente !== undefined) {
        itemExistente.quantidade = state.item.quantidade;
        itemExistente.precoUnitario = precoUnitario;
        itemExistente.valorTotal = state.item.valorTotal;
      } else {
        const item = {} as IPedidoItem;
        item.codigo = 0;
        item.codigoPedido = state.pedido.codigo;
        item.codigoProduto = state.produtoSelecionado.codigoProduto;
        item.codigoProdutoDefinicao = state.produtoSelecionado.codigoProdutoDefinicao;
        item.sku = state.produtoSelecionado.sku;
        item.produto = state.produtoSelecionado.nomeProduto;
        item.quantidade = state.item.quantidade;
        item.precoUnitario = precoUnitario;
        item.valorTotal = state.item.valorTotal;
        item.codigoErp = state.produtoSelecionado.codigoErp;

        state.pedido.itens.push(item);
      }

      calcularTotais();
      salvamentoAutomatico();

      state.tela = 'dados-principais';
      state.aba = 'itens';
    }

    function formatarQuantidade(valor: number) {
      return (Number(valor) || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    function aumentarQuantidade() {
      if (!pedidoEmDigitacao.value) {
        return;
      }

      if (state.produtoSelecionado.venderMultiplus > 0) {
        state.item.quantidade += state.produtoSelecionado.venderMultiplus;
      } else {
        state.item.quantidade += 1;
      }

      calcularItem();
      salvamentoAutomatico();
    }

    function diminuirQuantidade() {
      if (!pedidoEmDigitacao.value) {
        return;
      }

      if (state.produtoSelecionado.venderMultiplus > 0) {
        state.item.quantidade -= state.produtoSelecionado.venderMultiplus;
        if (state.item.quantidade <= 1) {
          state.item.quantidade = state.produtoSelecionado.venderMultiplus;
        }
      } else if (state.item.quantidade > 1) {
        state.item.quantidade -= 1;
      }

      calcularItem();
      salvamentoAutomatico();
    }

    function alterouQuantidade() {
      if (!pedidoEmDigitacao.value) {
        return;
      }

      if (state.produtoSelecionado.venderMultiplus > 0) {
        if (state.item.quantidade <= 1) {
          state.item.quantidade = state.produtoSelecionado.venderMultiplus;
        }
        clearTimeout(debounce);
        debounce = setTimeout(async () => {
          if (state.item.quantidade > 1) {
            const restante = state.item.quantidade % state.produtoSelecionado.venderMultiplus;
            if (restante !== 0) {
              const menorMultiplicador = state.item.quantidade - restante;
              const maiorMultiplicador = menorMultiplicador + state.produtoSelecionado.venderMultiplus;
              state.item.quantidade = (state.item.quantidade - menorMultiplicador < maiorMultiplicador - state.item.quantidade) ? menorMultiplicador : maiorMultiplicador;
            }
          }

          if (state.item.quantidade <= 1) {
            state.item.quantidade = state.produtoSelecionado.venderMultiplus;
          }

          calcularItem();
          salvamentoAutomatico();
        }, 600);
      } else if (state.item.quantidade < 1) {
        state.item.quantidade = 1;
      }

      calcularItem();
      salvamentoAutomatico();
    }

    function alterouItem() {
      if (!pedidoEmDigitacao.value) {
        return;
      }

      calcularItem();
      salvamentoAutomatico();
    }

    function alterouPercentualDesconto() {
      if (!pedidoEmDigitacao.value) {
        return;
      }

      calcularTotais();
      salvamentoAutomatico();
    }

    watch(async () => state.item.quantidade,
      async (novoValor) => {
        if (!novoValor || await novoValor < 1) {
          return;
        }

        calcularItem();
      });

    watch(() => state.pedido.codigoFormaPagamento,
      (novoValor, valorAnterior) => {
        if (novoValor === valorAnterior) {
          return;
        }

        salvamentoAutomatico();
      });

    function recuperarSalvamentoAutomatico() {
      if (UtilitarioGeral.valorValido(state.salvamentoAutomatico.dados)) {
        const pedidoSalvoAutomatico = JSON.parse(state.salvamentoAutomatico.dados);
        if (UtilitarioGeral.objetoValido(pedidoSalvoAutomatico)) {
          state.pedido = pedidoSalvoAutomatico;
          state.alterouPedido = true;
          state.recuperarSalvamentoAutomatico = false;
          apresentarMensagemSucesso('Informações recuperadas com sucesso!');
        }
      }
    }

    function obtemNumeroIdentificacao():string {
      if (state.pedido.codigo === 0) return '';

      if (UtilitarioGeral.valorValido(state.pedido.numeroErp)) {
        return `${state.pedido.numeroIdentificacao} ERP:${state.pedido.numeroErp}`;
      }
      return state.pedido.numeroIdentificacao;
    }

    return {
      appBase,
      state,
      EStatusPedido,
      tituloTela,
      clienteDescricao,
      pedidoEmDigitacao,
      apresentarAbaItens,
      apresentarAbaBuscarProdutos,
      totalItensQuantidade,
      valorProdutos,
      valorDesconto,
      salvarPedido,
      cancelarPedido,
      enviarPedido,
      deletarPedido,
      voltarTela,
      novoPedido,
      buscarProdutos,
      abrirItem,
      abrirItemPedido,
      removerItemPedido,
      repetirUltimaCompra,
      confirmarItem,
      calcularItem,
      calcularTotais,
      produtoJaAdicionado,
      UtilitarioMascara,
      formatarQuantidade,
      aumentarQuantidade,
      diminuirQuantidade,
      alterouQuantidade,
      alterouItem,
      alterouPercentualDesconto,
      salvamentoAutomatico,
      recuperarSalvamentoAutomatico,
      obtemNumeroIdentificacao,
    };
  },
});
